import React, { useEffect, useMemo, useRef, useState } from "react";
import { distinct } from "@/src/utils";
import { graphql, useFragment } from "react-relay";
import { TimeSeriesChartAndTable_data$key } from "./__generated__/TimeSeriesChartAndTable_data.graphql";
import { TimeSeriesChart } from "./TimeSeriesChart";

import { TimeSeriesTable } from "./TimeSeriesTable";
import { AnalyticsValueUnit } from "./__generated__/TimeSeriesOverviewItem_data.graphql";
import ParentSize from "@visx/responsive/lib/components/ParentSize";
import { ActivityIndicator, View } from "react-native";
import { useTheme } from "arkh";

import Animated from "react-native-reanimated";

export function TimeSeriesChartAndTable(props: {
  analytics: TimeSeriesChartAndTable_data$key;
  type?: "lines" | "bars";
  unit?: AnalyticsValueUnit;
  dimensionLabel?: string;
  loading?: boolean;
}) {
  const data = useFragment(
    graphql`
      fragment TimeSeriesChartAndTable_data on TimeSeriesAnalyticsConnection {
        ...TimeSeriesChart
        ...TimeSeriesTable
        dimensions {
          id
        }
      }
    `,
    props.analytics
  );
  const remoteDimensions = data.dimensions;
  const currentIds = useMemo(
    () => remoteDimensions?.map((d) => d.id) ?? [],
    [remoteDimensions]
  );
  const dimensionIdKey = currentIds.slice().sort().join(",");
  const prevIdsRef = useRef<Set<string> | null>(null);
  const currentIdsRef = useRef(currentIds);
  currentIdsRef.current = currentIds;

  const [selectedDimensions, setSelectedDimensions] = useState<string[]>(currentIds);

  useEffect(() => {
    const ids = currentIdsRef.current;
    const currentSet = new Set(ids);
    const prevSet = prevIdsRef.current;

    if (prevSet === null) {
      setSelectedDimensions(ids);
    } else {
      setSelectedDimensions((prev) => {
        const stillExist = prev.filter((id) => currentSet.has(id));
        const newIds = ids.filter((id) => !prevSet.has(id));
        return distinct([...stillExist, ...newIds]);
      });
    }

    prevIdsRef.current = currentSet;
  }, [dimensionIdKey]);
  const [hoverDimension, setHoverDimension] = useState<string | null>(null);

  return (
    <Animated.View
      style={{
        flex: 1,
        width: "100%",
        opacity: props.loading ? 0.5 : 1,
        transition: "opacity 0.3s ease-in-out",
        transitionDelay: "0.2s",
      }}
    >
      <View style={{ flex: 1, width: "100%" }}>
        <ParentSize debounceTime={10}>
          {(size) => (
            <TimeSeriesChart
              data={data}
              width={size.width}
              height={size.height}
              selectedDimensions={selectedDimensions}
              hoverDimension={hoverDimension}
              type={props.type}
              unit={props.unit}
            />
          )}
        </ParentSize>
      </View>
      <View style={{ flex: 1 }}>
        <TimeSeriesTable
          data={data}
          dimensionLabel={props.dimensionLabel}
          hoverDimension={hoverDimension}
          setHoverDimension={(e) => {
            setHoverDimension(e ?? null);
          }}
          activateDimensions={(ids) => {
            setSelectedDimensions((prev) => distinct([...ids, ...prev]));
          }}
          deactivateDimensions={(ids) => {
            setSelectedDimensions((prev) =>
              prev.filter((id) => !ids.includes(id))
            );
          }}
          selectedDimensions={selectedDimensions}
          unit={props.unit}
        />
      </View>
      <LoadingOverlay visible={!!props.loading} />
    </Animated.View>
  );
}

function LoadingOverlay(props: { visible: boolean }) {
  const theme = useTheme();

  return (
    <Animated.View
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(255, 255, 255, 0.5)",
        justifyContent: "center",
        alignItems: "center",
        pointerEvents: "none",
        opacity: props.visible ? 1 : 0,
        transition: "opacity 0.3s ease-in-out",
        transitionDelay: "0.4s",
      }}
    >
      <ActivityIndicator color={theme.darkText} size="small" />
    </Animated.View>
  );
}
